import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, NativeSelect, Segmented } from "@/components/common";
import { api, errMsg, todayStr, money } from "@/lib/api";

export default function PaymentDialog({ open, onOpenChange, kind = "collection", accountId, onSaved }) {
  const [f, setF] = useState({});
  const [accounts, setAccounts] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setF({ kind, account_id: accountId || "", date: todayStr(), amount: "", currency: "TRY", rate: 1, method: "cash", description: "" });
    api.get("/accounts").then((r) => setAccounts(r.data)).catch(() => {});
  }, [open, kind, accountId]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const acc = accounts.find((a) => a.id === f.account_id);

  const save = async (e) => {
    e.preventDefault();
    if (!f.account_id) return toast.error("Cari hesap seçin");
    setBusy(true);
    try {
      await api.post("/payments", { ...f, amount: Number(f.amount), rate: Number(f.rate) || 1 });
      toast.success(f.kind === "collection" ? "Tahsilat kaydedildi" : "Ödeme kaydedildi");
      onOpenChange(false);
      onSaved?.();
    } catch (e2) {
      toast.error(errMsg(e2));
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-[4px]" data-testid="payment-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">{f.kind === "collection" ? "Tahsilat Al" : "Ödeme Yap"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <Segmented value={f.kind} onChange={(v) => setF({ ...f, kind: v })} prefix="payment-kind" options={[["collection", "Tahsilat (Giriş)"], ["payment", "Ödeme (Çıkış)"]]} />
          <Field label="Cari Hesap">
            <NativeSelect value={f.account_id} onChange={set("account_id")} disabled={!!accountId} data-testid="payment-account-select">
              <option value="">Seçin…</option>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </NativeSelect>
            {acc && <span className="text-[11px] text-muted-foreground">Güncel bakiye: {money(Math.abs(acc.balance))} {acc.balance > 0 ? "(Borçlu)" : acc.balance < 0 ? "(Alacaklı)" : ""}</span>}
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Tutar" className="col-span-2"><Input required type="number" step="0.01" min="0.01" value={f.amount} onChange={set("amount")} data-testid="payment-amount-input" /></Field>
            <Field label="Para Birimi">
              <NativeSelect value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value, rate: e.target.value === "TRY" ? 1 : f.rate })} data-testid="payment-currency-select">
                <option value="TRY">TL ₺</option><option value="USD">USD $</option><option value="EUR">EUR €</option>
              </NativeSelect>
            </Field>
          </div>
          {f.currency !== "TRY" && (
            <Field label={`Kur (1 ${f.currency} = ? ₺)`}>
              <Input required type="number" step="0.0001" min="0.0001" value={f.rate} onChange={set("rate")} data-testid="payment-rate-input" />
              <span className="text-[11px] text-muted-foreground">TL karşılığı: {money((Number(f.amount) || 0) * (Number(f.rate) || 0))}</span>
            </Field>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tarih"><Input required type="date" value={f.date} onChange={set("date")} data-testid="payment-date-input" /></Field>
            <Field label="Yöntem">
              <NativeSelect value={f.method} onChange={set("method")} data-testid="payment-method-select">
                <option value="cash">Nakit</option><option value="bank">Banka / Havale</option><option value="card">Kredi Kartı</option><option value="check">Çek / Senet</option>
              </NativeSelect>
            </Field>
          </div>
          <Field label="Açıklama"><Input value={f.description} onChange={set("description")} data-testid="payment-desc-input" /></Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="payment-cancel-button">Vazgeç</Button>
            <Button type="submit" disabled={busy} data-testid="payment-save-button">Kaydet</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
